interface FreshchatUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
}

declare global {
  interface Window {
    fcWidget: {
      setExternalId: (id: string) => void;
      user: {
        setFirstName: (name: string) => void;
        setLastName: (name: string) => void;
        setEmail: (email: string) => void;
        setProperties: (properties: Record<string, any>) => void;
        clear: () => void;
      };
    };
  }
}

// Generate a persistent anonymous ID for users who aren't signed in
const getAnonymousId = (): string => {
  try {
    let anonymousId = localStorage.getItem('freshchat_anonymous_id');
    if (!anonymousId) {
      anonymousId = 'anon_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('freshchat_anonymous_id', anonymousId);
    }
    return anonymousId;
  } catch (error) {
    // Fallback if localStorage is not available
    console.warn('localStorage not available, using fallback anonymous ID');
    return 'anon_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }
};

// Clear anonymous ID when user signs in (to prevent conflicts)
const clearAnonymousId = (): void => {
  try {
    localStorage.removeItem('freshchat_anonymous_id');
  } catch (error) {
    console.warn('Could not clear anonymous ID from localStorage');
  }
};

export const identifyFreshchatUser = (user: FreshchatUser) => {
  if (typeof window !== 'undefined' && window.fcWidget) {
    try {
      // Clear any existing anonymous ID when user signs in
      clearAnonymousId();
      
      // Set external ID (unique identifier)
      window.fcWidget.setExternalId(user.id);
      
      // Set user properties
      if (user.email) {
        window.fcWidget.user.setFirstName(user.email);
        window.fcWidget.user.setEmail(user.email);
      }
    } catch (error) {
      console.error('Error identifying Freshchat user:', error);
    }
  }
};

export const setAnonymousUser = () => {
  if (typeof window !== 'undefined' && window.fcWidget) {
    try {
      const anonymousId = getAnonymousId();
      // Set external ID for anonymous users to preserve chat history
      window.fcWidget.setExternalId(anonymousId);
    } catch (error) {
      console.error('Error setting anonymous Freshchat user:', error);
    }
  }
};

export const resetFreshchatUser = () => {
  if (typeof window !== 'undefined' && window.fcWidget) {
    try {
      window.fcWidget.user.clear();
    } catch (error) {
      console.error('Error resetting Freshchat user:', error);
    }
  }
}; 